import { Enemy as IEnemy, Vector2 } from './types';
import { EnemyBullet, BulletHell } from './Bullet';

export abstract class BaseEnemy implements IEnemy {
  position: Vector2;
  velocity: Vector2;
  width: number;
  height: number;
  alive: boolean = true;
  health: number;
  score: number;
  shootTimer: number = 0;
  shootCooldown: number;
  protected time: number = 0;

  constructor(x: number, y: number, width: number, height: number, health: number, score: number, shootCooldown: number) {
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.width = width;
    this.height = height;
    this.health = health;
    this.score = score;
    this.shootCooldown = shootCooldown;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;
    this.shootTimer = Math.max(0, this.shootTimer - deltaTime);
    
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  abstract render(ctx: CanvasRenderingContext2D): void;
  abstract shoot(): (EnemyBullet | BulletHell)[];
}

export class InvaderEnemy extends BaseEnemy {
  private moveDirection: number = 1;
  private dropDistance: number = 30;

  constructor(x: number, y: number) {
    super(x, y, 40, 30, 1, 50, 2.0);
    this.velocity.x = 50;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    const margin = 50; // 画面端からの余白

    // 画面端手前で折り返し、一段下がる
    if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
      this.moveDirection *= -1;
      this.velocity.x = this.moveDirection * 50;
      this.position.y += this.dropDistance;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(this.position.x + 10, this.position.y + 5, 8, 8);
    ctx.fillRect(this.position.x + 22, this.position.y + 5, 8, 8);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 15, this.position.y + 15, 10, 5);
  }

  shoot(): EnemyBullet[] {
    if (this.shootTimer <= 0 && Math.random() < 0.01) {
      this.shootTimer = this.shootCooldown;
      return [new EnemyBullet(this.position.x + this.width / 2, this.position.y + this.height)];
    }
    return [];
  }
}

export class GalaxianEnemy extends BaseEnemy {
  private baseY: number;
  private attackMode: boolean = false;
  private attackTimer: number = 0;

  constructor(x: number, y: number) {
    super(x, y, 35, 35, 2, 150, 1.5);
    this.baseY = y;
    this.velocity.x = 30;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);

    if (!this.attackMode) {
      const margin = 50; // 画面端からの余白
      if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
        this.velocity.x *= -1;
      }
      
      if (Math.random() < 0.002) {
        this.attackMode = true;
        this.attackTimer = 3.0;
        this.velocity.x = 0;
        this.velocity.y = 150;
      }
    } else {
      this.attackTimer -= deltaTime;
      if (this.attackTimer <= 0 || this.position.y > 600) {
        this.attackMode = false;
        this.position.y = this.baseY;
        this.velocity.x = 30;
        this.velocity.y = 0;
      }
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    ctx.fillStyle = '#0000ff';
    ctx.fillRect(this.position.x + 8, this.position.y + 8, 6, 6);
    ctx.fillRect(this.position.x + 21, this.position.y + 8, 6, 6);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 12, this.position.y + 20, 11, 8);
  }

  shoot(): EnemyBullet[] {
    if (this.shootTimer <= 0 && (this.attackMode || Math.random() < 0.008)) {
      this.shootTimer = this.shootCooldown;
      return [new EnemyBullet(this.position.x + this.width / 2, this.position.y + this.height)];
    }
    return [];
  }
}

export class FallingEnemy extends BaseEnemy {
  private fallSpeed: number;

  constructor(x: number, y: number) {
    super(x, y, 30, 25, 1, 75, 1.8);
    this.fallSpeed = 80 + Math.random() * 60;
    this.velocity.y = this.fallSpeed;
    this.velocity.x = (Math.random() - 0.5) * 40;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    
    if (this.position.y > 650) {
      this.alive = false;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(this.position.x + 8, this.position.y + 5, 6, 6);
    ctx.fillRect(this.position.x + 16, this.position.y + 5, 6, 6);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 10, this.position.y + 15, 10, 5);
  }

  shoot(): EnemyBullet[] {
    if (this.shootTimer <= 0 && Math.random() < 0.015) {
      this.shootTimer = this.shootCooldown;
      return [new EnemyBullet(this.position.x + this.width / 2, this.position.y + this.height)];
    }
    return [];
  }
}

export class BossEnemy extends BaseEnemy {
  private phase: number = 1;
  private phaseTimer: number = 0;

  constructor(x: number, y: number) {
    super(x, y, 80, 60, 10, 1000, 0.8);
    this.velocity.x = 80;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.phaseTimer += deltaTime;
    const margin = 50; // 画面端からの余白

    // 画面端手前で折り返し、一段下がる
    if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
      this.velocity.x *= -1;
      this.position.y += 25; // ボスなので少し大きく下がる
    }

    if (this.phaseTimer > 10) {
      this.phase = (this.phase % 3) + 1;
      this.phaseTimer = 0;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#800080';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(this.position.x + 20, this.position.y + 15, 15, 15);
    ctx.fillRect(this.position.x + 45, this.position.y + 15, 15, 15);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 30, this.position.y + 40, 20, 10);
  }

  shoot(): (EnemyBullet | BulletHell)[] {
    if (this.shootTimer <= 0) {
      this.shootTimer = this.shootCooldown;
      
      const bullets: (EnemyBullet | BulletHell)[] = [];
      const centerX = this.position.x + this.width / 2;
      const centerY = this.position.y + this.height;

      switch (this.phase) {
        case 1:
          bullets.push(new EnemyBullet(centerX - 10, centerY));
          bullets.push(new EnemyBullet(centerX + 10, centerY));
          break;
        case 2:
          for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            bullets.push(new BulletHell(centerX, centerY, angle));
          }
          break;
        case 3:
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2 + this.time;
            bullets.push(new BulletHell(centerX, centerY, angle, 100));
          }
          break;
      }
      return bullets;
    }
    return [];
  }
}

export class KamikazeEnemy extends BaseEnemy {
  private hasLeftFormation: boolean = false;
  private targetPlayer: boolean = false;
  
  constructor(x: number, y: number) {
    super(x, y, 35, 30, 2, 200, 2.5);
    this.velocity.x = 40;
  }
  
  update(deltaTime: number, playerX?: number, playerY?: number): void {
    super.update(deltaTime);
    
    if (!this.hasLeftFormation) {
      const margin = 50; // 画面端からの余白
      if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
        this.velocity.x *= -1;
      }
      
      if (Math.random() < 0.003 || (playerX && Math.abs(this.position.x - playerX) < 50)) {
        this.hasLeftFormation = true;
        this.targetPlayer = true;
        this.velocity.y = 200;
      }
    } else if (this.targetPlayer && playerX && playerY) {
      const dx = playerX - this.position.x;
      const dy = playerY - this.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        const speed = 250;
        this.velocity.x = (dx / distance) * speed;
        this.velocity.y = (dy / distance) * speed;
      }
    }
    
    if (this.position.y > 650) {
      this.alive = false;
    }
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.position.x + this.width / 2, this.position.y + this.height / 2);
    
    const angle = Math.atan2(this.velocity.y, this.velocity.x) + Math.PI / 2;
    ctx.rotate(angle);
    
    ctx.fillStyle = '#ff6600';
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(-6, -10, 4, 4);
    ctx.fillRect(2, -10, 4, 4);
    
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(-5, 0, 10, 10);
    
    ctx.restore();
  }
  
  shoot(): EnemyBullet[] {
    if (this.hasLeftFormation && this.shootTimer <= 0 && Math.random() < 0.02) {
      this.shootTimer = this.shootCooldown;
      return [new EnemyBullet(this.position.x + this.width / 2, this.position.y + this.height)];
    }
    return [];
  }
}

export class ArcEnemy extends BaseEnemy {
  private startX: number;
  private startY: number;
  private arcRadius: number = 150;
  private arcAngle: number = 0;
  private arcSpeed: number = 1.5;
  
  constructor(x: number, y: number, fromRight: boolean = false) {
    super(x, y, 40, 35, 3, 350, 1.8);
    this.startX = x;
    this.startY = y;
    this.arcAngle = fromRight ? Math.PI : 0;
    this.arcSpeed = fromRight ? -1.5 : 1.5;
  }
  
  update(deltaTime: number): void {
    this.time += deltaTime;
    this.shootTimer = Math.max(0, this.shootTimer - deltaTime);
    
    this.arcAngle += this.arcSpeed * deltaTime;
    
    this.position.x = this.startX + Math.cos(this.arcAngle) * this.arcRadius;
    this.position.y = this.startY + Math.sin(this.arcAngle) * this.arcRadius * 0.5;
    
    this.velocity.x = -Math.sin(this.arcAngle) * this.arcRadius * this.arcSpeed;
    this.velocity.y = Math.cos(this.arcAngle) * this.arcRadius * 0.5 * this.arcSpeed;
    
    if (this.position.y > 650 || this.position.x < -50 || this.position.x > 850) {
      this.alive = false;
    }
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.position.x + this.width / 2, this.position.y + this.height / 2);
    
    const angle = Math.atan2(this.velocity.y, this.velocity.x);
    ctx.rotate(angle);
    
    ctx.fillStyle = '#9900ff';
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(-8, -8, 6, 6);
    ctx.fillRect(2, -8, 6, 6);
    
    ctx.fillStyle = '#00ffff';
    ctx.fillRect(-10, 5, 20, 8);
    
    ctx.restore();
  }
  
  shoot(): EnemyBullet[] {
    if (this.shootTimer <= 0 && Math.random() < 0.015) {
      this.shootTimer = this.shootCooldown;
      const bullets: EnemyBullet[] = [];
      for (let i = -1; i <= 1; i++) {
        const angle = Math.atan2(this.velocity.y, this.velocity.x) + Math.PI / 2 + i * 0.3;
        const speed = 150;
        bullets.push(new EnemyBullet(
          this.position.x + this.width / 2,
          this.position.y + this.height,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        ));
      }
      return bullets;
    }
    return [];
  }
}

export class SniperEnemy extends BaseEnemy {
  private aimTime: number = 0;
  private isAiming: boolean = false;
  private targetAngle: number = 0;
  
  constructor(x: number, y: number) {
    super(x, y, 45, 40, 4, 500, 4.0);
    this.velocity.y = 50;
  }
  
  update(deltaTime: number, playerX?: number, playerY?: number): void {
    super.update(deltaTime);
    
    if (this.position.y > 100 && this.position.y < 150) {
      this.velocity.y = 0;
    }
    
    if (this.velocity.y === 0 && playerX && playerY) {
      const dx = playerX - this.position.x;
      const dy = playerY - this.position.y;
      this.targetAngle = Math.atan2(dy, dx);
      
      if (!this.isAiming) {
        this.isAiming = true;
        this.aimTime = 1.0;
      }
      
      if (this.aimTime > 0) {
        this.aimTime -= deltaTime;
      }
    }
    
    const margin = 50; // 画面端からの余白
    if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
      this.velocity.x *= -1;
    }
    
    if (this.position.y > 650) {
      this.alive = false;
    }
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#ff0066';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + 10, this.position.y + 8, 8, 8);
    ctx.fillRect(this.position.x + 27, this.position.y + 8, 8, 8);
    
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(this.position.x + 17, this.position.y + 25, 11, 10);
    
    if (this.isAiming && this.aimTime > 0) {
      ctx.save();
      ctx.strokeStyle = '#ff0000';
      ctx.globalAlpha = 0.5 + 0.5 * Math.sin(this.time * 10);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(this.position.x + this.width / 2, this.position.y + this.height);
      ctx.lineTo(
        this.position.x + this.width / 2 + Math.cos(this.targetAngle) * 100,
        this.position.y + this.height + Math.sin(this.targetAngle) * 100
      );
      ctx.stroke();
      ctx.restore();
    }
  }
  
  shoot(): EnemyBullet[] {
    if (this.isAiming && this.aimTime <= 0 && this.shootTimer <= 0) {
      this.shootTimer = this.shootCooldown;
      this.isAiming = false;
      
      const speed = 300;
      return [new EnemyBullet(
        this.position.x + this.width / 2,
        this.position.y + this.height,
        Math.cos(this.targetAngle) * speed,
        Math.sin(this.targetAngle) * speed
      )];
    }
    return [];
  }
}

// 新しい紫敵（ステージ4用）
export class PurpleEnemy extends BaseEnemy {
  private burstTimer: number = 0;

  constructor(x: number, y: number) {
    super(x, y, 60, 50, 8, 800, 0.6); // 高耐久、高得点、適度な射撃間隔（0.1→0.6）
    this.velocity.x = 40;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.burstTimer += deltaTime;
    const margin = 50; // 画面端からの余白

    // 画面端手前で折り返し、一段下がる
    if (this.position.x <= margin || this.position.x >= (800 - this.width - margin)) {
      this.velocity.x *= -1;
      this.position.y += 30;
    }
  }

  shoot(): (EnemyBullet | BulletHell)[] {
    if (this.shootTimer <= 0) {
      this.shootTimer = this.shootCooldown;
      
      const bullets: (EnemyBullet | BulletHell)[] = [];
      const centerX = this.position.x + this.width / 2;
      const centerY = this.position.y + this.height;
      
      // 5way弾を撃つ（速度をさらに遅く調整）
      for (let i = 0; i < 5; i++) {
        const angle = (i - 2) * 0.3; // -0.6から0.6まで
        const bullet = new EnemyBullet(centerX, centerY);
        bullet.velocity.x = Math.sin(angle) * 70; // 100から70に減速
        bullet.velocity.y = Math.cos(angle) * 70; // 100から70に減速
        bullets.push(bullet);
      }
      
      return bullets;
    }
    return [];
  }

  render(ctx: CanvasRenderingContext2D): void {
    // 紫色で描画
    ctx.fillStyle = '#800080';
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    // 光る効果
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(this.position.x + 10, this.position.y + 10, this.width - 20, this.height - 20);
    
    // 中央の光点
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(this.position.x + this.width/2 - 5, this.position.y + this.height/2 - 5, 10, 10);
  }
}